import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Search, PackageCheck } from "lucide-react";

// MG piece register — Handwoven + Quality — loaded from MG_Rugs-_Datasheet.xlsx.
// Filter by category / status / weaver / PO. Quality rows show TANA × PAUGAZI
// as separate numeric fields per acceptance.
export default function MGPieces() {
  const { lang } = useLang();
  const t = (en, hi) => (lang === "en" ? en : hi);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (status) params.status = status;
    const r = await api.get("/mg/pieces", { params });
    setRows(r.data || []);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/mg/pieces/summary").then((r) => setSummary(r.data));
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [category, status]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((p) =>
      [p.weaver_name, p.po_number, p.carpet_no, p.design, p.colour, p.invoice_number, p.quality]
        .filter(Boolean).some((x) => String(x).toLowerCase().includes(s))
    );
  }, [rows, q]);

  const statuses = ["In production", "Ready", "Delivered to HAG", "Ready for Invoice", "Invoiced"];

  return (
    <div data-testid="mg-pieces">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
            <PackageCheck size={22} /> {t("MG Piece register", "MG पीस रजिस्टर")}
          </h1>
          <p className="text-xs text-[#7C8E9E]">
            {t("Loaded from MG_Rugs Datasheet — Handwoven + Quality Stock. TANA × PAUGAZI shown as separate fields for Quality.",
               "MG डेटाशीट से लोड — हैंडवोवन + क्वालिटी। क्वालिटी में TANA × PAUGAZI अलग-अलग संख्यात्मक।")}
          </p>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <MiniStat label={t("Pieces", "पीस")} v={summary.totals.total_pieces} testid="mg-pieces-total" />
          <MiniStat label={t("Sq ft", "स्क्वेयर फीट")} v={summary.totals.total_sq_ft.toFixed(0)} />
          <MiniStat label={t("Weight kg", "वज़न कि.")} v={summary.totals.total_weight_kg.toFixed(0)} />
          <MiniStat label={t("Weavers", "बुनकर")} v={summary.totals.weavers} />
          <MiniStat label={t("POs", "PO")} v={summary.totals.pos} />
        </div>
      )}

      <div className="hag-surface p-3 mb-3 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[220px] relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#7C8E9E]" />
          <input data-testid="mg-pieces-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t("Search weaver / PO / MGR # / design / colour", "बुनकर / PO / MGR # / डिज़ाइन / रंग खोजें")}
            className="w-full h-10 pl-8 pr-3 border border-[#1B3A5C]/15 rounded-md text-sm" />
        </div>
        <select data-testid="mg-pieces-category" value={category} onChange={(e) => setCategory(e.target.value)}
          className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
          <option value="">{t("All categories", "सभी")}</option>
          <option value="Handwoven">Handwoven</option>
          <option value="Quality">Quality</option>
        </select>
        <select data-testid="mg-pieces-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
          <option value="">{t("All statuses", "सभी स्थिति")}</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="text-xs text-[#4A627A] ml-auto">{filtered.length} {t("rows", "पंक्ति")}</div>
      </div>

      {loading ? <div className="text-sm text-[#7C8E9E]">Loading…</div> : (
        <div className="hag-surface overflow-x-auto" data-testid="mg-pieces-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#7C8E9E] bg-[#FAF6F0]">
                <th className="text-left px-3 py-2">Cat.</th>
                <th className="text-left px-3 py-2">Weaver</th>
                <th className="text-left px-3 py-2">PO#</th>
                <th className="text-left px-3 py-2">MGR#</th>
                <th className="text-left px-3 py-2">Tana × Paugazi</th>
                <th className="text-left px-3 py-2">Size</th>
                <th className="text-right px-3 py-2">Sq ft</th>
                <th className="text-right px-3 py-2">Wt kg</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 300).map((p) => (
                <tr key={p.id} className="border-t border-[#1B3A5C]/10" data-testid={`mg-piece-${p.id}`}>
                  <td className="px-3 py-1.5">
                    <span className={`status-chip ${p.category === "Quality" ? "grade-A" : "grade-B"}`}>{p.category[0]}</span>
                  </td>
                  <td className="px-3 py-1.5 text-xs">{p.weaver_name}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{p.po_number}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{p.carpet_no || "—"}</td>
                  <td className="px-3 py-1.5 text-xs" data-testid={`mg-piece-tp-${p.id}`}>
                    {p.category === "Quality" ? (
                      <><b>{p.tana}</b> × <b>{p.paugazi}</b></>
                    ) : (
                      <span className="text-[#7C8E9E]">Handwoven</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-xs">{p.size_text || "—"}</td>
                  <td className="px-3 py-1.5 text-right text-xs">{p.sq_ft ? p.sq_ft.toFixed(1) : "—"}</td>
                  <td className="px-3 py-1.5 text-right text-xs">{p.weight_kg ? p.weight_kg.toFixed(1) : "—"}</td>
                  <td className="px-3 py-1.5">
                    <span className={`status-chip ${
                      p.mg_status === "Invoiced" ? "pace-green"
                      : p.mg_status === "Ready for Invoice" ? "pace-amber"
                      : "pace-none"
                    }`}>{p.mg_status}</span>
                  </td>
                  <td className="px-3 py-1.5 text-xs">{p.invoice_number || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 300 && (
            <div className="p-3 text-xs text-[#7C8E9E] text-center">
              {t(`… ${filtered.length - 300} more rows. Use search or filters to narrow.`,
                 `… और ${filtered.length - 300} पंक्तियाँ। खोज या फ़िल्टर से घटाएँ।`)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, v, testid }) {
  return (
    <div className="hag-surface p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: "var(--brand-navy)" }}>{v}</div>
    </div>
  );
}
